import { useState, useEffect, useCallback, useMemo } from 'react';
import { SavChat, ChatStatus } from '@/models/savChat';
import { SavChatRepository } from '@/services/firebase/sav-chat-repository';

/**
 * Hook pour gérer tous les chats SAV d'une organisation
 */
export const useSavChats = (organizationId: string | null) => {
  const [chats, setChats] = useState<SavChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Écouter les changements des chats en temps réel
  useEffect(() => {
    if (!organizationId) {
      setChats([]);
      setLoading(false);
      return;
    }

    console.log('📡 Subscribing to organization chats:', organizationId);
    setLoading(true);

    const unsubscribe = SavChatRepository.subscribeToOrganizationChats(
      organizationId,
      (updatedChats) => {
        setChats(updatedChats);
        setLoading(false);
      }
    );

    return () => {
      console.log('🔌 Unsubscribing from organization chats:', organizationId);
      unsubscribe();
    };
  }, [organizationId]);

  /**
   * Calculer les statistiques des chats
   */
  const stats = useMemo(() => {
    const open = chats.filter((c) => c.status === ChatStatus.OPEN).length;
    const inProgress = chats.filter((c) => c.status === ChatStatus.IN_PROGRESS).length;
    const closed = chats.filter((c) => c.status === ChatStatus.CLOSED).length;
    const unreadCount = chats.reduce((sum, c) => sum + c.unreadByAdmin, 0);

    return {
      open,
      inProgress,
      closed,
      unreadCount,
      total: chats.length,
    };
  }, [chats]);

  /**
   * Mettre à jour le statut d'un chat
   */
  const updateStatus = useCallback(async (chatId: string, status: ChatStatus) => {
    try {
      await SavChatRepository.updateChatStatus(chatId, status);
      console.log('✅ Statut du chat mis à jour');
    } catch (err) {
      console.error('❌ Erreur lors de la mise à jour du statut:', err);
      setError('Erreur lors de la mise à jour du statut');
      throw err;
    }
  }, []);

  /**
   * Récupérer un chat par son ID
   */
  const getChatById = useCallback(
    (chatId: string) => {
      return chats.find((c) => c.id === chatId) || null;
    },
    [chats]
  );

  /**
   * Récupérer les chats par statut
   */
  const getChatsByStatus = useCallback(
    (status: ChatStatus) => {
      return chats.filter((c) => c.status === status);
    },
    [chats]
  );

  return {
    chats,
    loading,
    error,
    stats,
    updateStatus,
    getChatById,
    getChatsByStatus,
  };
};

